import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { FC, useMemo } from 'react'

import { Accordion } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../../../../lib/analytics'
import { isValidDocument } from '../../../../../../lib/utils/contentful'
import { FitModelStateContainer } from '../../contexts/FitModelStateContainer'
import { ProductDetailsCopy } from '../styles'

import { IProps } from './IProps'
import {
  AccordionContainer,
  AccordionTitle,
  CareInstructions,
  FabricAndCare,
  FeaturesWrapper,
  FitModelsDescriptionsList,
  ListItem,
  ProductDetailsAndFitBody,
  RawMaterials,
  SectionWrapper,
} from './styles'

export const ProductDetailsAccordionSection: FC<IProps> = ({
  careInstructions,
  fitModelsDescriptions,
  productGroupRtfFeatures,
  productRtfFeatures,
  rawMaterials,
  upc,
}) => {
  const {
    detailsSection: {
      accordionTitles: { fabricAndCareTitle, productDetailsTitle },
    },
  } = useTranslation()

  const { fitModel } = FitModelStateContainer.useContainer()
  const computedFitModelDescriptions = useMemo(() => {
    // If there is an active fit model, do not print out model descriptions
    if (fitModel?.description) return []

    return fitModelsDescriptions ? fitModelsDescriptions : []
  }, [fitModel?.description, fitModelsDescriptions])
  return (
    <SectionWrapper>
      <AccordionContainer
        {...trackEvent({ category: 'product details', action: 'click accordion' })}
      >
        <Accordion variant='light' icon='plus' ariaLabel='Detail Section' isTransparent showBorder>
          <Accordion.Section index={0}>
            <Accordion.Header>
              <AccordionTitle textTransform='uppercase' asTag='p'>
                {productDetailsTitle}
              </AccordionTitle>
            </Accordion.Header>
            <Accordion.Body>
              <ProductDetailsAndFitBody>
                {computedFitModelDescriptions.length > 0 && (
                  <FitModelsDescriptionsList>
                    {computedFitModelDescriptions.map((fitModelsDescription, index) => (
                      <ListItem key={index}>{fitModelsDescription}</ListItem>
                    ))}
                  </FitModelsDescriptionsList>
                )}
                <div data-testid='product-details'>
                  <FeaturesWrapper>
                    {isValidDocument(productGroupRtfFeatures) && (
                      <div data-testid='product-group-features'>
                        {documentToReactComponents(productGroupRtfFeatures)}
                      </div>
                    )}

                    {isValidDocument(productRtfFeatures) && (
                      <div datat-test-id='product-features'>
                        {documentToReactComponents(productRtfFeatures)}
                      </div>
                    )}
                  </FeaturesWrapper>

                  {upc && <ProductDetailsCopy secondary>UPC: {upc}</ProductDetailsCopy>}
                </div>
              </ProductDetailsAndFitBody>
            </Accordion.Body>
          </Accordion.Section>
          {rawMaterials?.length || careInstructions ? (
            <Accordion.Section index={2}>
              <Accordion.Header>
                <AccordionTitle textTransform='uppercase' asTag='p'>
                  {fabricAndCareTitle}
                </AccordionTitle>
              </Accordion.Header>
              <Accordion.Body>
                <FabricAndCare data-testid='fabric-and-care'>
                  {rawMaterials?.length ? (
                    <RawMaterials>
                      {rawMaterials.map((material, index) => (
                        <ListItem key={index}>{material}</ListItem>
                      ))}
                    </RawMaterials>
                  ) : null}

                  {careInstructions && <CareInstructions>{careInstructions}</CareInstructions>}
                </FabricAndCare>
              </Accordion.Body>
            </Accordion.Section>
          ) : null}
        </Accordion>
      </AccordionContainer>
    </SectionWrapper>
  )
}
